import type { Member } from '@/data/seedMember'
import type { RawUser } from '@/types/api'

/**
 * Maps API DTOs (snake_case, possibly null) to the view-model the connected
 * space consumes. This is the defensive boundary: every nullable field gets a
 * safe fallback and arrays are never indexed without a guard (see the null/empty
 * robustness convention in the plan).
 */

/** The subset of `Member` that `GET /me` can populate (profile + address). */
export type MemberProfile = Pick<
  Member,
  'id' | 'civility' | 'firstName' | 'lastName' | 'email' | 'memberNumber' | 'birthDate' | 'nir' | 'phone' | 'address'
>

/**
 * Map the current user (`GET /me`) to the profile slice of `Member`.
 *
 * ⚠️ Only profile + first address: contracts, banking details, third-party-payer
 * card and documents come from other endpoints (later lots). The caller merges
 * this over the existing member so the not-yet-wired parts are preserved.
 */
export function mapUserProfile(user: RawUser): MemberProfile {
  const address = user.addresses?.[0]
  return {
    id: user.id ?? '',
    civility: user.civility,
    firstName: user.firstname ?? '',
    lastName: user.lastname ?? '',
    email: user.email ?? '',
    memberNumber: user.inter_platforms_reference ?? '',
    birthDate: user.birthdate ?? '',
    nir: user.social_security_number ?? '',
    phone: user.phone_number ?? '',
    address: {
      line1: address?.address_line1 ?? '',
      line2: address?.address_line2 ?? '',
      postalCode: address?.zipcode ?? '',
      city: address?.city ?? '',
      country: address?.country ?? '',
    },
  }
}
